import { memo, useCallback, useMemo, useRef } from "react";

import { ICatalogue } from "../../../apis/useGetCatalogueData/types";
import {
	IVirtualCartItem,
	findBaseCartIndexForProduct,
	getVirtualItemsForProduct,
	isCustomizableOrSet,
} from "../../../common/helpers/cartVirtualItem";
import { displayCentsWithDollarSign } from "../../../common/helpers/stringUtils";
import Condition from "../../../components/Conditional/Condition";
import I18nText from "../../../components/I18nText/I18nText";
import OOSIndicator from "../../../components/OOSIndicator/OOSIndicator";
import { Image } from "../../../components/elements";
import QuantitySelector from "../../../components/elements/Selection/quantity/QuantitySelector";
import {
	useCartActions,
	useCartContext,
} from "../../../contexts/CartServiceContext/CartServiceContext";
import { useConfigContext } from "../../../contexts/ConfigContext/ConfigContext";
import { IStallMode } from "../../../contexts/ConfigContext/types";
import { MAX_CART_ITEM_QUANTITY } from "../../CartPage/constants";
import { PAGE_PARAM } from "../../constants";
import { ICategoryList } from "../types";
import AddToCartBtn from "./AddToCartBtn/AddToCartBtn";
import "./ProductSection.scss";
import "./ProductSection_torikizoku.scss";
import { useHandleClickProduct } from "./hooks";

interface IProductSectionProps {
	category: ICategoryList & { products: ICatalogue[] };
	sectionRefs: React.MutableRefObject<Map<string, HTMLDivElement | null>>;
}

type ICartActions = ReturnType<typeof useCartActions>;
type IItemClickHook = ReturnType<typeof useHandleClickProduct>;

interface IVariantRowProps {
	item: IVirtualCartItem;
	product: ICatalogue;
	cartActions: ICartActions;
	handleClickItem: IItemClickHook;
}

// Fire the same handler on Enter/Space so click-driven divs are keyboard
// operable (SonarCloud S1082 / S6848).
const activateOnKey =
	(handler: (e: React.SyntheticEvent) => void) =>
	(e: React.KeyboardEvent): void => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			handler(e);
		}
	};

const VariantRow: React.FC<IVariantRowProps> = ({
	item,
	product,
	cartActions,
	handleClickItem,
}) => {
	const handleEdit = handleClickItem(
		product.brand_menu_item_id,
		PAGE_PARAM.EDIT,
		item.index.toString()
	);

	const handleQuantityChange = useCallback(
		(qty: number) => {
			if (qty <= 0) {
				cartActions.REMOVE_CART(item.index);

				return;
			}
			cartActions.UPDATE_CART_ITEM({
				index: item.index,
				item: { ...item.line, quantity: qty },
			});
		},
		[cartActions, item]
	);

	const stopProp = useCallback((e: React.SyntheticEvent) => {
		e.stopPropagation();
	}, []);

	return (
		<li className="VariantRow" data-testid={`variant-row-${item.index}`}>
			<div
				className="VariantLabel"
				role="button"
				tabIndex={0}
				onClick={handleEdit}
				onKeyDown={activateOnKey(handleEdit)}
			>
				<I18nText className="VariantLabelText" enText={item.label.en} cnText={item.label.cn} />
			</div>
			<div className="VariantQuantity" role="presentation" onClick={stopProp} onKeyDown={stopProp}>
				<QuantitySelector
					key={`vqs-${item.index}-${item.line.quantity}`}
					max={MAX_CART_ITEM_QUANTITY}
					initValue={item.line.quantity}
					showZero={true}
					onChange={handleQuantityChange}
					testid={`variant-${item.index}`}
				/>
			</div>
		</li>
	);
};

interface IProductCardProps {
	product: ICatalogue;
	cart: ICatalogue[];
	cartActions: ICartActions;
	handleClickItem: IItemClickHook;
	isFeatureOn: boolean;
}

// Unified card component. Same outer <div> always, so CSS transitions on
// `border-color`, `max-height`, and `opacity` can drive a smooth entry AND
// exit when the customizable item flips between "no cart line" (small) and
// "has cart lines" (big). The variant area stays in the DOM and collapses
// via max-height + opacity instead of unmounting.
const ProductCard: React.FC<IProductCardProps> = ({
	product,
	cart,
	cartActions,
	handleClickItem,
	isFeatureOn,
}) => {
	const isCustomizable = isCustomizableOrSet(product);
	const baseLineIndex = useMemo(
		() => (!isCustomizable && isFeatureOn ? findBaseCartIndexForProduct(product, cart) : -1),
		[isCustomizable, isFeatureOn, product, cart]
	);
	const baseLine = baseLineIndex >= 0 ? cart[baseLineIndex] : null;
	const baseQty = baseLine?.quantity ?? 0;

	const virtualItems = useMemo(
		() => (isFeatureOn && isCustomizable ? getVirtualItemsForProduct(product, cart) : []),
		[isFeatureOn, isCustomizable, product, cart]
	);
	const hasVariants = virtualItems.length > 0;

	const navToDetailNew = handleClickItem(product.brand_menu_item_id);

	const handleBaseQuantityChange = useCallback(
		(qty: number) => {
			if (baseLineIndex < 0) {
				if (qty > 0) {
					cartActions.ADD_ITEM({ ...product, quantity: 1 });
				}

				return;
			}

			if (qty <= 0) {
				cartActions.REMOVE_CART(baseLineIndex);

				return;
			}

			if (baseLine) {
				cartActions.UPDATE_CART_ITEM({
					index: baseLineIndex,
					item: { ...baseLine, quantity: qty },
				});
			}
		},
		[cartActions, baseLineIndex, baseLine, product]
	);

	const handleCardTap = useCallback(
		(e: React.SyntheticEvent) => {
			if (isFeatureOn && !isCustomizable) {
				// Base item card body tap: add 1 (no detail-page round trip).
				handleBaseQuantityChange(Math.min(baseQty + 1, MAX_CART_ITEM_QUANTITY));

				return;
			}
			navToDetailNew(e);
		},
		[isFeatureOn, isCustomizable, baseQty, handleBaseQuantityChange, navToDetailNew]
	);

	const stopProp = useCallback((e: React.SyntheticEvent) => {
		e.stopPropagation();
	}, []);

	const renderQuantityArea = () => {
		if (!isFeatureOn || isCustomizable) {
			return <AddToCartBtn onClick={navToDetailNew} />;
		}

		if (baseQty === 0) {
			return <AddToCartBtn onClick={() => handleBaseQuantityChange(1)} />;
		}

		return (
			<QuantitySelector
				key={`qs-${product.brand_menu_item_id}-${baseQty}`}
				max={MAX_CART_ITEM_QUANTITY}
				initValue={baseQty}
				showZero={true}
				inventoryCount={product.inventory_count}
				onChange={handleBaseQuantityChange}
				testid={`product-${product.brand_menu_item_id}`}
			/>
		);
	};

	const isOos = product.inventory_count === 0;
	const className = `ProductItem ProductCard ${hasVariants ? "IsBig" : "IsSmall"}`;

	return (
		<div
			data-testid={`product-item-${product.brand_menu_item_id}`}
			className={className}
			role="button"
			tabIndex={0}
			onClick={handleCardTap}
			onKeyDown={activateOnKey(handleCardTap)}
			aria-disabled={isOos || undefined}
		>
			<Condition check={hasVariants}>
				<span hidden data-testid={`product-card-big-${product.brand_menu_item_id}`} />
			</Condition>
			<div className="MainRow">
				<div className="ProductImageContainer">
					<Condition check={isOos}>
						<OOSIndicator data-testid={"oos-indicator-" + product.brand_menu_item_id} />
					</Condition>
					<Image className="ProductItemImage" src={product.product_image} />
				</div>
				<div className="ProductInfo">
					<I18nText className="ProductTitle" enText={product.title} cnText={product.title_cn} />
					<div className="ProductPriceWrapper">
						<p className="ProductPrice">{displayCentsWithDollarSign(product.price)}</p>
						<div
							className="QuantityWrapper"
							role="presentation"
							onClick={stopProp}
							onKeyDown={stopProp}
						>
							{renderQuantityArea()}
						</div>
					</div>
				</div>
			</div>
			<div className="VariantContainer" aria-hidden={!hasVariants}>
				<div className="VariantDivider" />
				<ul className="VariantList">
					{virtualItems.map((vi) => (
						<VariantRow
							key={`variant-${vi.index}-${vi.line.id}`}
							item={vi}
							product={product}
							cartActions={cartActions}
							handleClickItem={handleClickItem}
						/>
					))}
				</ul>
			</div>
		</div>
	);
};

const ProductSection: React.FC<IProductSectionProps> = memo(({ category, sectionRefs }) => {
	const sectionElementRef = useRef<HTMLDivElement | null>(null);
	const handleClickItem = useHandleClickProduct();
	const cartActions = useCartActions();
	const { state: cartRaw } = useCartContext((state) => state.cart);
	const { state: allowVirtualItems } = useConfigContext(
		(state) => state.config?.allow_virtual_items
	);
	const { state: showCategoryDescription } = useConfigContext(
		(state) => state.config?.show_category_description
	);
	const { state: stallMode } = useConfigContext((state) => state.stallMode);
	const cart = useMemo(() => cartRaw ?? [], [cartRaw]);

	const setRefs = useCallback(
		(node: HTMLDivElement | null) => {
			sectionElementRef.current = node;
			sectionRefs.current.set(category.id, node);
		},
		[sectionRefs, category.id]
	);

	const isFeatureOn = allowVirtualItems === true;
	const hasCategoryDescription =
		stallMode === IStallMode.TORIKIZOKU &&
		showCategoryDescription === true &&
		!!(category.description || category.description_cn);

	return (
		<div ref={setRefs} className="ProductCategorySection" id={`category-${category.id}`}>
			<I18nText className="CategoryTitle" enText={category.name} cnText={category.name_cn} />
			{hasCategoryDescription && (
				<I18nText
					className="CategoryDescription"
					enText={category.description ?? ""}
					cnText={category.description_cn ?? ""}
				/>
			)}
			<div className={`ProductGrid${isFeatureOn ? " ProductGridFeatureOn" : ""}`}>
				{category?.products?.map((product) => {
					// Per FOOD-2476: per-item React key is composed from the
					// category id and the product's JSON:API id so that the same
					// product can appear in its real-category section AND in one or
					// more virtual sections at the same time without colliding on
					// React's reconciliation key.
					const cardKey = `${category.id}:${product.id}`;

					return (
						<ProductCard
							key={cardKey}
							product={product}
							cart={cart}
							cartActions={cartActions}
							handleClickItem={handleClickItem}
							isFeatureOn={isFeatureOn}
						/>
					);
				})}
			</div>
		</div>
	);
});

export default ProductSection;
